import type { ChangeEvent, CompositionEvent, InputHTMLAttributes, Ref } from 'react';
import { toLatinDigits } from '@workbook/shared';

/**
 * 🔴 THE TEXT BOX — design-system plan DS-5 (5.1; F48, F126, F153, F533, F424).
 *
 * `kind` says what the box holds, and the box does the rest:
 * - `amount` and `percent`: the decimal keypad on a phone, the figure face, right-aligned;
 * - `quantity`: the decimal keypad, right-aligned tabular figures in the text face;
 * - `integer`: the number keypad, right-aligned tabular figures;
 * - `text` (the default): nothing special.
 * A number kind maps Bengali digits to Latin AS THEY ARE TYPED (`toLatinDigits`, the same length, so the cursor stays
 * put) — `Number('১২০')` is NaN, and a quantity typed that way was dropped without a word (F424). Not DURING an input
 * method's composition, though: a Bangla phonetic keyboard composes `১২` before committing it, and rewriting the box
 * mid-composition made the browser commit the text a second time (`11212` — DS-5 review); the digits are mapped once,
 * when the composition ends. For a number the screen must check, use `NumberInput`, which also says when the text is
 * not one.
 *
 * `amount` (a boolean) is the old spelling of `kind="amount"`, kept for its 57 callers. `width` sizes the box — never an
 * inline style (F48, DS-9c): see `BoxWidth`. The caller's class is kept, and so is its `ref`.
 * Readonly, disabled and invalid each have a look of their own (components.css, the control layer).
 */
export type InputKind = 'text' | 'amount' | 'quantity' | 'integer' | 'percent';

/**
 * How wide a text box or a select is — design-system plan DS-9c. Never an inline style: the lint fails on one.
 * - `full` (the default): the width of its column or cell;
 * - `auto`: as wide as its content, for a toolbar;
 * - a step of the scale, for a value of a known length, in characters of the box's own face: `xs` 7ch (a currency
 *   code, a position, a digit count), `sm` 9ch (a rate, a prefix, hours), `md` 12ch (an amount, a count of days),
 *   `lg` 16ch (a code), `xl` 22ch (a reference, a request id). 7ch is the floor: the padding is inside the width, and
 *   below it a three-letter code clips. An amount's box is set in the figure face, so a step is a little wider there.
 * Every step holds in a form table too (components.css: each class has a twin after `.wb-table--form .wb-input`).
 */
export type BoxWidth = 'full' | 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const INPUT_WIDTH: Record<BoxWidth, string> = {
  full: '',
  auto: 'wb-input--auto',
  xs: 'wb-input--w-xs',
  sm: 'wb-input--w-sm',
  md: 'wb-input--w-md',
  lg: 'wb-input--w-lg',
  xl: 'wb-input--w-xl',
};

const KIND_CLASS: Record<InputKind, string> = {
  text: '',
  amount: 'wb-input--amount',
  percent: 'wb-input--amount',
  quantity: 'wb-input--num',
  integer: 'wb-input--num',
};

export function isNumberKind(kind: InputKind): boolean {
  return kind !== 'text';
}

/** Map the box's Bengali digits in place, keeping the caret where it was; true when anything changed. */
function mapDigits(box: HTMLInputElement): boolean {
  const latin = toLatinDigits(box.value);
  if (latin === box.value) return false;
  // selectionStart is null on a type without a caret (number, date) — and reading it there may throw.
  let at: number | null = null;
  try {
    at = box.selectionStart;
  } catch {
    at = null;
  }
  box.value = latin;
  if (at !== null) {
    try {
      box.setSelectionRange(at, at);
    } catch {
      // A type that refuses a selection keeps the browser's caret.
    }
  }
  return true;
}

export function Input({
  kind: kindProp,
  amount,
  width,
  className,
  onChange,
  onCompositionEnd,
  ref,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'width'> & {
  kind?: InputKind;
  /** The old spelling of `kind="amount"`. */
  amount?: boolean;
  width?: BoxWidth;
  ref?: Ref<HTMLInputElement>;
}) {
  const kind: InputKind = kindProp ?? (amount ? 'amount' : 'text');
  const classes = ['wb-input', KIND_CLASS[kind], width ? INPUT_WIDTH[width] : '', className ?? ''].filter(Boolean).join(' ');
  const inputMode = kind === 'integer' ? 'numeric' : isNumberKind(kind) ? 'decimal' : undefined;
  const numeric = onChange !== undefined && isNumberKind(kind);
  const change = numeric
    ? (e: ChangeEvent<HTMLInputElement>) => {
        if (!(e.nativeEvent as InputEvent).isComposing) mapDigits(e.target);
        onChange(e);
      }
    : onChange;
  const compositionEnd = numeric
    ? (e: CompositionEvent<HTMLInputElement>) => {
        onCompositionEnd?.(e);
        // The committed text arrives with no further change event: map it now, and tell the screen.
        if (mapDigits(e.currentTarget)) onChange({ ...e, target: e.currentTarget } as unknown as ChangeEvent<HTMLInputElement>);
      }
    : onCompositionEnd;
  return <input inputMode={inputMode} {...props} ref={ref} className={classes} onChange={change} onCompositionEnd={compositionEnd} />;
}
Input.wbLabellable = true;
